import { getLocalizedStringValue } from '@griply/utils';
import React, { useMemo } from 'react';
import { useNewEntities } from 'shared/hooks/use-new-entities';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { LifeArea, LocalizedLifeArea } from 'shared/types/life-area';

import { DataContext, DataContextProps } from './data-context';
import { TaskCreate, TaskCreateProps } from './task-create';
import { TaskDetail, TaskDetailProps } from './task-detail';

type ContextualCreationData = Omit<
  DataContextProps['data'],
  'lifeAreas' | 'goalsToCreate' | 'tagsToCreate'
> & {
  lifeAreas: (LifeArea | LocalizedLifeArea)[];
};

export type DetailDataProviderProps = TaskDetailProps & {
  contextualCreationData: ContextualCreationData;
};

export const DetailDataProvider: React.FC<DetailDataProviderProps> = ({
  contextualCreationData,
  ...rest
}) => {
  const locale = useI18nLocale();
  const { goalsToCreate, tagsToCreate, addGoalToCreate, addTagToCreate } =
    useNewEntities();

  const _goals = useMemo(
    () => [...goalsToCreate, ...contextualCreationData.goals],
    [contextualCreationData.goals, goalsToCreate],
  );
  const _tags = useMemo(
    () => [...tagsToCreate, ...contextualCreationData.tags],
    [contextualCreationData.tags, tagsToCreate],
  );

  const lifeAreas = useMemo(
    () =>
      contextualCreationData.lifeAreas.map(({ name, ...rest }) => ({
        ...rest,
        name: getLocalizedStringValue(name, locale),
      })),
    [contextualCreationData.lifeAreas, locale],
  );

  const value = useMemo(
    () => ({
      data: {
        ...contextualCreationData,
        lifeAreas,
        goals: _goals,
        tags: _tags,
        goalsToCreate,
        tagsToCreate,
      },
      addGoalToCreate,
      addTagToCreate,
    }),
    [
      _goals,
      _tags,
      addGoalToCreate,
      addTagToCreate,
      contextualCreationData,
      goalsToCreate,
      lifeAreas,
      tagsToCreate,
    ],
  );

  return (
    <DataContext.Provider value={value}>
      <TaskDetail {...rest} />
    </DataContext.Provider>
  );
};

export type CreateDataProviderProps = TaskCreateProps & {
  contextualCreationData: ContextualCreationData;
};

export const CreateDataProvider: React.FC<CreateDataProviderProps> = ({
  contextualCreationData,
  ...rest
}) => {
  const locale = useI18nLocale();
  const { goalsToCreate, tagsToCreate, addGoalToCreate, addTagToCreate } =
    useNewEntities();

  const _goals = useMemo(
    () => [...goalsToCreate, ...contextualCreationData.goals],
    [contextualCreationData.goals, goalsToCreate],
  );
  const _tags = useMemo(
    () => [...tagsToCreate, ...contextualCreationData.tags],
    [contextualCreationData.tags, tagsToCreate],
  );

  const lifeAreas = useMemo(
    () =>
      contextualCreationData.lifeAreas.map(({ name, ...rest }) => ({
        ...rest,
        name: getLocalizedStringValue(name, locale),
      })),
    [contextualCreationData.lifeAreas, locale],
  );

  const value = useMemo(
    () => ({
      data: {
        ...contextualCreationData,
        lifeAreas,
        goals: _goals,
        tags: _tags,
        goalsToCreate,
        tagsToCreate,
      },
      addGoalToCreate,
      addTagToCreate,
    }),
    [
      _goals,
      _tags,
      addGoalToCreate,
      addTagToCreate,
      contextualCreationData,
      goalsToCreate,
      lifeAreas,
      tagsToCreate,
    ],
  );

  return (
    <DataContext.Provider value={value}>
      <TaskCreate {...rest} />
    </DataContext.Provider>
  );
};
